"use client";

import { useEffect, useState } from "react";
import { SOL_MINT } from "@/lib/revive-create/sol-constants";
import {
  getCachedDexScreenerEmbedPath,
  setCachedDexScreenerEmbedPath,
} from "@/lib/token-dex/dexScreenerPairCache";

/** Query string DexScreener uses for compact chart embeds (theme + chrome flags). */
const DEX_EMBED_QUERY =
  "?embed=1&loadChartSettings=0&trades=0&tabs=0&info=0" +
  "&chartLeftToolbar=0&chartTheme=dark&theme=dark&chartStyle=1";

const PAIR_FETCH_TIMEOUT_MS = 8_000;
const PAIR_FETCH_MAX_ATTEMPTS = 2;
const PAIR_RETRY_DELAY_MS = 400;

/**
 * Resolve best pair via our API (server DexScreener lookup + cache).
 * @param {string} mint
 * @param {AbortSignal} [signal]
 */
async function fetchPrimaryPairViaApi(mint, signal) {
  const res = await fetch(`/api/token-dex/dex-pair?mint=${encodeURIComponent(mint.trim())}`, {
    cache: "no-store",
    signal,
  });
  if (!res.ok) return null;
  const data = await res.json();
  return typeof data?.pairAddress === "string" && data.pairAddress.trim()
    ? data.pairAddress.trim()
    : null;
}

/**
 * @param {string} mint
 * @param {AbortSignal} outerSignal
 * @returns {Promise<string|null>}
 */
async function fetchPrimaryPairWithRetry(mint, outerSignal) {
  let lastPair = null;

  for (let attempt = 0; attempt < PAIR_FETCH_MAX_ATTEMPTS; attempt++) {
    if (outerSignal.aborted) return lastPair;
    if (attempt > 0) {
      await new Promise((resolve) => {
        const id = window.setTimeout(resolve, PAIR_RETRY_DELAY_MS);
        if (outerSignal.aborted) {
          window.clearTimeout(id);
          resolve();
          return;
        }
        outerSignal.addEventListener(
          "abort",
          () => {
            window.clearTimeout(id);
            resolve();
          },
          { once: true },
        );
      });
      if (outerSignal.aborted) return lastPair;
    }

    const ac = new AbortController();
    const onParentAbort = () => ac.abort();
    outerSignal.addEventListener("abort", onParentAbort);
    let timeoutId = 0;

    try {
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = window.setTimeout(() => {
          reject(Object.assign(new Error("timeout"), { name: "TimeoutError" }));
        }, PAIR_FETCH_TIMEOUT_MS);
      });

      const pair = await Promise.race([
        fetchPrimaryPairViaApi(mint, ac.signal),
        timeoutPromise,
      ]);
      window.clearTimeout(timeoutId);
      lastPair = pair;
      if (pair) return pair;
    } catch {
      window.clearTimeout(timeoutId);
      if (outerSignal.aborted) return lastPair;
    } finally {
      outerSignal.removeEventListener("abort", onParentAbort);
      try {
        ac.abort();
      } catch {
        /* noop */
      }
    }
  }

  return lastPair;
}

function buildEmbedSrc(pathAddress) {
  const seg = encodeURIComponent(pathAddress);
  return `https://dexscreener.com/solana/${seg}${DEX_EMBED_QUERY}`;
}

/** Embeds DexScreener for the given Solana mint. Falls back to SOL when mint is missing. Parent should be `relative` with explicit height. */
export function DexScreenerChart({ address }) {
  const mint =
    typeof address === "string" && address.trim() ? address.trim() : SOL_MINT;

  const cachedPair = (() => {
    const hit = getCachedDexScreenerEmbedPath(mint);
    if (!hit || hit === mint) return null;
    return hit;
  })();
  const [resolvedPath, setResolvedPath] = useState(() => cachedPair);
  const [status, setStatus] = useState(() => (cachedPair ? "ready" : "loading"));

  useEffect(() => {
    let cancelled = false;
    const hit = getCachedDexScreenerEmbedPath(mint);
    const cachedPairHit = hit && hit !== mint ? hit : null;
    if (cachedPairHit) {
      setResolvedPath(cachedPairHit);
      setStatus("ready");
      return () => {
        cancelled = true;
      };
    }

    setResolvedPath(null);
    setStatus("loading");

    const ac = new AbortController();

    (async () => {
      try {
        const pair = await fetchPrimaryPairWithRetry(mint, ac.signal);
        if (cancelled) return;
        if (pair) {
          setCachedDexScreenerEmbedPath(mint, pair);
          setResolvedPath(pair);
          setStatus("ready");
          return;
        }
        setStatus("empty");
      } catch {
        if (cancelled) return;
        setStatus("empty");
      }
    })();

    return () => {
      cancelled = true;
      try {
        ac.abort();
      } catch {
        /* noop */
      }
    };
  }, [mint]);

  if (status !== "ready" || !resolvedPath) {
    return (
      <div className="absolute inset-0 box-border flex h-full max-h-full w-full max-w-full items-center justify-center overflow-hidden bg-[#0b0e13]">
        <p className="text-sm text-gray-400">
          {status === "empty" ? "No DexScreener pool found for this mint yet." : "Loading pair…"}
        </p>
      </div>
    );
  }

  const src = buildEmbedSrc(resolvedPath);

  return (
    <div className="absolute inset-0 box-border h-full max-h-full w-full max-w-full overflow-hidden">
      <iframe
        key={src}
        src={src}
        title="DexScreener chart"
        className="absolute inset-0 z-0 box-border h-full w-full border-0"
        allow="clipboard-write"
        loading="eager"
        referrerPolicy="strict-origin-when-cross-origin"
      />
      <div
        className="pointer-events-auto absolute bottom-0 left-0 z-[5] h-16 w-52 bg-transparent"
        aria-hidden
      />
      <div
        className="pointer-events-auto absolute bottom-0 right-0 z-[5] h-20 w-56 bg-transparent"
        aria-hidden
      />
      <div
        className="pointer-events-auto absolute right-0 top-0 z-[5] h-14 w-48 bg-transparent"
        aria-hidden
      />
    </div>
  );
}
